import { api } from './config';

// SYNC generator demonstrator (sc-6314). Documents are model-produced
// markdown; the page renders them and never reaches into payloads.
// PDF export + revision turns: sc-6344. The v0.4 one-page format (sc-6408):
// a structured `page` the server draws as a PDF, plus the rep's `notes`.
// District SYNCs (sc-6439): a doc's subject is a branch (location_id) or a
// CRM district (crm_org_unit_id), exactly one of the two.

export type SyncDocumentStatus = 'queued' | 'running' | 'completed' | 'failed';
export type SyncPdfKind = 'customer' | 'talk';

export interface SyncDocument {
  id: number;
  // exactly one is set: a branch run or a district run (sc-6439); payloads
  // from before districts carry no crm_org_unit_id
  location_id: number | null;
  crm_org_unit_id?: string | null;
  status: SyncDocumentStatus;
  rep_context: string | null;
  // A revision (sc-6344) is a child run of a completed parent: which run it
  // revises and what the rep asked to change. Both null on a root run — and
  // absent on payloads from before this shipped, which read as roots.
  revised_from_id: number | null;
  feedback: string | null;
  // pre-v0.4 rows: the two markdown bodies (null on v0.4 rows)
  customer_doc: string | null;
  talk_track: string | null;
  // v0.4 (sc-6408): the page the PDF is drawn from — the UI never renders it
  // itself, it shows the server's PDF — and the rep's markdown notes. Null on
  // pre-v0.4 rows, and absent on payloads from before this shipped.
  page?: Record<string, unknown> | null;
  notes?: string | null;
  error: string | null;
  model: string | null;
  turns: number | null;
  // token totals for the run: input_tokens, output_tokens,
  // cache_creation_input_tokens, cache_read_input_tokens (sc-6338)
  usage: Record<string, number> | null;
  started_at: string | null;
  finished_at: string | null;
  duration_seconds: number | null;
  user: { id: number; email: string; name: string | null } | null;
  created_at: string;
  updated_at: string;
}

// list rows (index and recent) omit the document bodies; both carry the
// creator (sc-6437), which only the recent list renders, and a district
// row's district name from its page header (sc-6439)
export type SyncDocumentSummary = Pick<
  SyncDocument,
  | 'id'
  | 'location_id'
  | 'crm_org_unit_id'
  | 'status'
  | 'rep_context'
  | 'revised_from_id'
  | 'feedback'
  | 'error'
  | 'model'
  | 'turns'
  | 'usage'
  | 'started_at'
  | 'finished_at'
  | 'duration_seconds'
  | 'user'
  | 'created_at'
  | 'updated_at'
> & { district_name?: string | null };

// The picker's options (sc-6439): districts with a CRM roster, each with
// every branch label; Edge branches, each labeled off its district's roster.
export interface SyncDistrictSubject {
  crm_org_unit_id: string;
  name: string;
  company: string;
  labels: string[];
}

export interface SyncBranchSubject {
  location_id: number;
  name: string;
  label: string;
  company: string | null;
  district: string | null;
  // the branch's district when the picker offers it, else null
  crm_org_unit_id: string | null;
}

export interface SyncSubjects {
  districts: SyncDistrictSubject[];
  branches: SyncBranchSubject[];
}

export const syncDocumentsApi = {
  create: (locationId: number, repContext: string) =>
    api
      .post<SyncDocument>('/sync_documents', {
        location_id: locationId,
        rep_context: repContext || null,
      })
      .then((res) => res.data),

  createForDistrict: (crmOrgUnitId: string, repContext: string) =>
    api
      .post<SyncDocument>('/sync_documents', {
        crm_org_unit_id: crmOrgUnitId,
        rep_context: repContext || null,
      })
      .then((res) => res.data),

  subjects: () => api.get<SyncSubjects>('/sync_documents/subjects').then((res) => res.data),

  getOne: (id: number) => api.get<SyncDocument>(`/sync_documents/${id}`).then((res) => res.data),

  listForLocation: (locationId: number) =>
    api
      .get<SyncDocumentSummary[]>('/sync_documents', { params: { location_id: locationId } })
      .then((res) => res.data),

  listForDistrict: (crmOrgUnitId: string) =>
    api
      .get<SyncDocumentSummary[]>('/sync_documents', { params: { crm_org_unit_id: crmOrgUnitId } })
      .then((res) => res.data),

  // The no-branch view [sc-6437]: the 50 newest completed runs across
  // branches, each revision chain as its latest completed revision only.
  listRecent: () => api.get<SyncDocumentSummary[]>('/sync_documents/recent').then((res) => res.data),

  // Branded PDF of one stored body [sc-6344]: blob GET via the shared axios
  // instance so it carries the bearer token (a raw browser download would
  // not). Returns the bytes + the server's filename for an object-URL download.
  pdf: async (id: number, doc: SyncPdfKind): Promise<{ blob: Blob; filename: string }> => {
    const res = await api.get(`/sync_documents/${id}/pdf`, { params: { doc }, responseType: 'blob' });
    const disposition = (res.headers['content-disposition'] as string | undefined) ?? '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return {
      blob: res.data as Blob,
      filename: match?.[1] ?? `sync-${doc === 'talk' ? 'talk-track' : 'customer'}-${id}.pdf`,
    };
  },

  // The v0.4 one-page PDF [sc-6408]: same token-authenticated blob GET, no
  // doc param. `overflow` is the server's fit problem for this page (the
  // block that runs past the sheet, or the word count), URI-encoded in a
  // header; null when the page fits.
  pagePdf: async (id: number): Promise<{ blob: Blob; filename: string; overflow: string | null }> => {
    const res = await api.get(`/sync_documents/${id}/pdf`, { responseType: 'blob' });
    const disposition = (res.headers['content-disposition'] as string | undefined) ?? '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const overflow = res.headers['x-sync-page-overflow'] as string | undefined;
    return {
      blob: res.data as Blob,
      filename: match?.[1] ?? `sync-${id}.pdf`,
      overflow: overflow ? decodeURIComponent(overflow) : null,
    };
  },

  // A revision turn [sc-6344]: a child run that replays the parent's
  // transcript and asks for the change. 201 with the child's doc JSON.
  revise: (id: number, feedback: string) =>
    api.post<SyncDocument>(`/sync_documents/${id}/revise`, { feedback }).then((res) => res.data),
};
